import { API_ORIGIN } from '../config/apiConfig';

/**
 * Profile Image Utilities
 * 
 * Standardized utilities for handling profile images across the application.
 * This ensures consistent behavior and proper URL construction for profile images.
 */

/**
 * Constructs the proper URL for a profile image.
 *
 * This is the ONLY way an avatar image URL should be built — every person chip
 * in the app routes through it, so a stored value is resolved identically
 * everywhere. It handles the three shapes the API can return (an absolute URL, a
 * proxied `/api/users/profile-picture/:id` path needing the API origin, or an
 * untransformed S3 key that has to be rebuilt from `userId`) and returns
 * `undefined` when there's nothing renderable, which is what makes callers fall
 * back to initials.
 *
 * `profilePicture` accepts `null` because that is what the API sends for a user
 * with no picture — widening it here keeps call sites from each having to
 * normalize with `?? undefined`.
 *
 * @param profilePicture - The profile picture path/URL from the user object
 * @param userId - Used to rebuild the proxied URL when the stored value is a raw key
 * @returns The complete URL for the profile image, or undefined if no image
 */
export const getProfileImageUrl = (profilePicture?: string | null, userId?: string): string | undefined => {
  // If no profile picture is set, return undefined (will show initials)
  if (!profilePicture) {
    return undefined;
  }

  // If it's already a complete URL (starts with http), return as-is
  if (profilePicture.startsWith('http')) {
    return profilePicture;
  }

  // If it's already a proxy URL (starts with /api/users/profile-picture/), convert to full URL
  if (profilePicture.startsWith('/api/users/profile-picture/')) {
    const cleanBaseUrl = API_ORIGIN;
    return `${cleanBaseUrl}${profilePicture}`;
  }

  // If no userId, return undefined (will show initials)
  if (!userId) {
    return undefined;
  }

  // Use the proxy endpoint for profile pictures (S3 keys or local paths)
  const cleanBaseUrl = API_ORIGIN;
  return `${cleanBaseUrl}/api/users/profile-picture/${userId}`;
};

/**
 * Gets user initials for fallback display
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @param email - User's email (fallback)
 * @returns User initials or first letter of email
 */
export const getUserInitials = (firstName?: string, lastName?: string, email?: string): string => {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  
  if (firstName) {
    return firstName[0].toUpperCase();
  }
  
  if (email) {
    return email[0].toUpperCase();
  }
  
  return '?';
};

/**
 * Gets user display name
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @param email - User's email (fallback)
 * @returns User's display name
 */
export const getUserDisplayName = (firstName?: string, lastName?: string, email?: string): string => {
  const fullName = `${firstName || ''} ${lastName || ''}`.trim();
  return fullName || email || 'Unknown User';
};
